import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { comics, categories } from '@/mocks/comics';
import { Comic } from '@/mocks/comics';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function CategoryPage() {
  const { slug = '全部' } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const filteredComics: Comic[] = useMemo(() => {
    let result = slug === '全部' ? [...comics] : comics.filter(c => c.categories.includes(slug));

    switch (sortBy) {
      case 'popular':
        result.sort((a, b) => b.views - a.views);
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'latest':
      default:
        result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        break;
    }

    return result;
  }, [slug, sortBy]);

  const catDescriptions: Record<string, string> = {
    '全部': '八仔漫画网为您收录了海量精品漫画，涵盖热血、恋爱、玄幻、搞笑、悬疑、科幻等多种类型，免费在线阅读，无广告干扰。',
    '热血': '热血漫画专区为你精选最燃最爽的战斗冒险故事，从校园对决到异世界征战，每一页都让你热血沸腾。',
    '恋爱': '恋爱漫画专区收录最甜蜜动人的爱情故事，从校园初恋到奇幻情缘，每一段感情都令人心动不已。',
    '玄幻': '玄幻漫画专区带你进入异界大陆的奇幻世界，仙侠修真、魔法斗气，无尽想象尽在其中。',
    '搞笑': '搞笑漫画专区收集最轻松幽默的欢乐故事，神仙妖怪的日常、兄妹互坑的趣事，笑到停不下来。',
    '悬疑': '悬疑漫画专区汇聚扣人心弦的推理谜题，从古装奇案到现代谜局，层层反转，烧脑不停。',
    '科幻': '科幻漫画专区展现未来世界的科技幻想，末日生存、星际冒险，带你探索未知的宇宙。',
    '校园': '校园漫画专区记录青春洋溢的校园生活，同桌日常、社团活动，每一页都是最美好的回忆。',
    '竞技': '竞技漫画专区聚焦热血沸腾的竞技比拼，电竞对决、体育竞赛，见证冠军的诞生。',
    '日常': '日常漫画专区分享温暖治愈的日常生活，妖怪上班、猫咪冒险，每一话都让人会心一笑。',
    '历史': '历史漫画专区重现波澜壮阔的历史传奇，武侠江湖、乱世英雄，带你穿越时空感受传奇。',
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${slug}漫画 - 八仔漫画网`,
    description: catDescriptions[slug] || '八仔漫画网漫画分类页面',
    url: `https://bazaimanhua.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '八仔漫画网',
      url: 'https://bazaimanhua.com',
    },
  };

  return (
    <>
      <SEOHead
        title={`${slug}漫画 - 在线免费阅读 - 八仔漫画网`}
        description={catDescriptions[slug] || '八仔漫画网漫画分类页面，免费在线阅读各类漫画。'}
        keywords={`${slug}漫画,在线免费阅读,八仔漫画网,${slug}漫画大全`}
        canonicalUrl={`https://bazaimanhua.com/category/${slug}`}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '漫画分类', to: '/category/全部' },
                { label: slug },
              ]}
            />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="mb-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
              {slug === '全部' ? '全部漫画' : `${slug}漫画`}
            </h1>
            <p className="text-sm text-foreground-500 max-w-2xl">
              {catDescriptions[slug] || '八仔漫画网为您提供免费的漫画在线阅读体验。'}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex flex-wrap gap-1.5">
              {categories.map((cat) => (
                <Link
                  key={cat}
                  to={`/category/${cat}`}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    slug === cat
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200'
                  }`}
                >
                  {cat}
                </Link>
              ))}
            </div>

            <div className="flex items-center gap-2 bg-background-100 rounded-full p-1">
              {[
                { key: 'latest' as const, label: '最新' },
                { key: 'popular' as const, label: '最热' },
                { key: 'rating' as const, label: '评分' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    sortBy === opt.key
                      ? 'bg-background-50 text-foreground-900 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                <i className="ri-emotion-sad-line text-foreground-400 text-2xl"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类下暂无漫画，请浏览其他分类</p>
              <Link
                to="/category/全部"
                className="inline-block mt-4 px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
              >
                查看全部漫画
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}